import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight, Plus, Minus, Trash2, Search, Save, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransferAddItemsPage() {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const [load, setLoad] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchQ, setSearchQ] = useState("");

  const fetchLoad = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/internal-transfers/${transferId}`);
      setLoad(r.data);
      setRows(r.data.transfer_items?.length > 0 ? r.data.transfer_items.map(ti => ({ ...ti })) : []);
    } catch { toast.error("Transfer not found"); navigate("/internal-transfers"); }
    finally { setLoading(false); }
  }, [transferId, navigate]);

  const fetchItems = useCallback(async () => {
    try { const r = await axios.get(`${API}/items`); setItems(r.data); } catch {}
  }, []);

  useEffect(() => { fetchLoad(); fetchItems(); }, [fetchLoad, fetchItems]);

  const filteredItems = items.filter(i => {
    if (!searchQ) return true;
    return i.name?.toLowerCase().includes(searchQ.toLowerCase()) || i.sku?.toLowerCase().includes(searchQ.toLowerCase());
  }).slice(0, 30);

  const addItem = (item) => {
    if (rows.some(r => r.item_id === item.item_id)) { toast.error("Item already added"); return; }
    setRows(r => [...r, { item_id: item.item_id, item_name: item.name, planned_quantity: 1, uom: item.unit || "CS" }]);
    setSearchQ("");
  };

  const updateRow = (idx, field, value) => setRows(r => r.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  const removeRow = (idx) => setRows(r => r.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/internal-transfers/${transferId}/transfer-items`, { transfer_items: rows, user: "John Doe" });
      toast.success(`${rows.length} items saved`);
      navigate(`/internal-transfers/${transferId}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-60px)]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-600" /></div>;
  if (!load) return null;

  return (
    <div className="flex flex-col h-[calc(100vh-60px)]" data-testid="add-items-page">
      {/* Breadcrumb */}
      <div className="px-6 py-2 border-b border-zinc-200 bg-white">
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500">
          <button onClick={() => navigate("/internal-transfers")} className="hover:text-zinc-800">Internal Transfers</button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => navigate(`/internal-transfers/${transferId}`)} className="hover:text-zinc-800">{load.to_number}</button>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-800 font-medium">Manage Items</span>
        </nav>
      </div>

      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200 bg-white flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-zinc-900">{load.to_number} — Transfer Items</h1>
          <p className="text-xs text-zinc-500 mt-0.5">{load.description}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/internal-transfers/${transferId}`)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-items-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : `Save ${rows.length} Items`}</Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Item search */}
        <div className="w-1/3 border-r border-zinc-200 p-4 overflow-y-auto bg-zinc-50">
          <h3 className="text-xs font-bold text-zinc-700 uppercase mb-2">Add Items</h3>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <Input placeholder="Search items by name or SKU..." className="pl-8 h-9 text-xs" value={searchQ} onChange={e => setSearchQ(e.target.value)} data-testid="item-search" />
          </div>
          <div className="space-y-1">
            {filteredItems.map(item => {
              const alreadyAdded = rows.some(r => r.item_id === item.item_id);
              return (
                <button key={item.item_id} onClick={() => !alreadyAdded && addItem(item)} disabled={alreadyAdded}
                  data-testid={`item-${item.item_id}`}
                  className={`w-full text-left p-2.5 rounded border text-xs transition-colors ${alreadyAdded ? "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-default" : "bg-white border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 cursor-pointer"}`}>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{item.sku ? `SKU: ${item.sku} · ` : ""}{item.unit || "CS"} · {item.category || ""}</div>
                  {alreadyAdded && <span className="text-[10px] text-emerald-600">Added</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Selected items */}
        <div className="flex-1 p-5 overflow-y-auto">
          <h3 className="text-xs font-bold text-zinc-700 uppercase mb-3">Transfer Items ({rows.length})</h3>
          {rows.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-sm border-2 border-dashed border-zinc-200 rounded-lg">
              Search and select items from the left panel
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="selected-items-table">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Item</th>
                    <th className="text-right px-4 py-2.5 font-medium w-40">Planned Quantity</th>
                    <th className="text-left px-4 py-2.5 font-medium w-24">UOM</th>
                    <th className="px-4 py-2.5 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t border-zinc-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-900">{row.item_name}</div>
                        <div className="text-[10px] text-zinc-500">{row.item_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateRow(i, "planned_quantity", Math.max(1, row.planned_quantity - 1))}><Minus className="w-3 h-3" /></Button>
                          <Input type="number" value={row.planned_quantity} onChange={e => updateRow(i, "planned_quantity", parseInt(e.target.value) || 1)}
                            className="h-8 w-20 text-center font-bold text-sm" data-testid={`qty-${i}`} />
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateRow(i, "planned_quantity", row.planned_quantity + 1)}><Plus className="w-3 h-3" /></Button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={row.uom} onValueChange={v => updateRow(i, "uom", v)}>
                          <SelectTrigger className="h-8 text-xs w-20"><SelectValue /></SelectTrigger>
                          <SelectContent>{["CS", "EA", "LB", "PL"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600" data-testid={`remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
